import React, { useState, useEffect } from "react";
import "./style.css";
import { host } from "../../config/Config";

export const AdminEducationSlots = () => {
    const [educationSlots, setEducationSlots] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [formData, setFormData] = useState({
        educationType: "",
        slotDate: "",
        startTime: "",
        endTime: "",
        maxCapacity: 1,
        availableSlots: 1,
        status: "ACTIVE",
    });
    const [message, setMessage] = useState({ text: "", type: "" });
    const [deleteDialog, setDeleteDialog] = useState({
        show: false,
        slotId: null,
        applicationsCount: 0,
    });

    const educationTypes = {
        "hands-on": "Hands On",
        "look-and-learn": "Look and Learn",
        "one-on-one": "One on One",
        fade: "Fade Workshop",
        formation: "Formation Workshop",
        "creative-formation": "Creative Formation",
        fundamental: "Fundamental Workshop",
        online: "Online Education",
        "nine-week": "Nine Week Program",
        "twelve-week": "Twelve Week Program",
    };

    useEffect(() => {
        fetchEducationSlots();
    }, []);

    const fetchEducationSlots = async () => {
        try {
            const response = await fetch(`${host}/api/v1/education-slots`, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem(
                        "jwt-vlada"
                    )}`,
                },
            });

            if (response.ok) {
                const data = await response.json();
                // Sort: ACTIVE first, then by date descending
                const sorted = data.sort((a, b) => {
                    if (a.status === "ACTIVE" && b.status !== "ACTIVE")
                        return -1;
                    if (a.status !== "ACTIVE" && b.status === "ACTIVE")
                        return 1;
                    return new Date(b.slotDate) - new Date(a.slotDate);
                });
                setEducationSlots(sorted);
            } else {
                showMessage("Greška prilikom učitavanja slotova", "error");
            }
        } catch (error) {
            console.error("Error fetching slots:", error);
            showMessage("Greška prilikom učitavanja slotova", "error");
        } finally {
            setLoading(false);
        }
    };

    const handleCreate = async () => {
        try {
            const response = await fetch(`${host}/api/v1/education-slots`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${localStorage.getItem(
                        "jwt-vlada"
                    )}`,
                },
                body: JSON.stringify({
                    educationType: formData.educationType,
                    slotDate: formData.slotDate,
                    startTime: formData.startTime,
                    endTime: formData.endTime,
                    maxCapacity: parseInt(formData.maxCapacity),
                }),
            });

            if (response.ok) {
                showMessage(
                    "Slot uspešno kreiran i termini su blokirani",
                    "success"
                );
                setShowCreateForm(false);
                resetForm();
                fetchEducationSlots();
            } else {
                showMessage("Greška prilikom kreiranja slota", "error");
            }
        } catch (error) {
            console.error("Error creating slot:", error);
            showMessage("Greška prilikom kreiranja slota", "error");
        }
    };

    const handleUpdate = async (id) => {
        try {
            const response = await fetch(
                `${host}/api/v1/education-slots/${id}`,
                {
                    method: "PUT",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${localStorage.getItem(
                            "jwt-vlada"
                        )}`,
                    },
                    body: JSON.stringify({
                        educationType: formData.educationType,
                        slotDate: formData.slotDate,
                        startTime: formData.startTime,
                        endTime: formData.endTime,
                        maxCapacity: parseInt(formData.maxCapacity),
                        status: formData.status,
                    }),
                }
            );

            if (response.ok) {
                showMessage(
                    "Slot uspešno ažuriran. Termini su ažurirani.",
                    "success"
                );
                setEditingId(null);
                resetForm();
                fetchEducationSlots();
            } else {
                showMessage("Greška prilikom ažuriranja slota", "error");
            }
        } catch (error) {
            console.error("Error updating slot:", error);
            showMessage("Greška prilikom ažuriranja slota", "error");
        }
    };

    const handleDelete = async (id) => {
        try {
            // First, check how many applications will be affected
            const countResponse = await fetch(
                `${host}/api/v1/education-slots/${id}/applications-count`,
                {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem(
                            "jwt-vlada"
                        )}`,
                    },
                }
            );

            if (!countResponse.ok) {
                showMessage("Greška prilikom provere prijava", "error");
                return;
            }

            const applicationsCount = await countResponse.json();

            // Show dialog with count info
            setDeleteDialog({
                show: true,
                slotId: id,
                applicationsCount: applicationsCount,
            });
        } catch (error) {
            console.error("Error checking applications:", error);
            showMessage("Greška prilikom provere prijava", "error");
        }
    };

    const confirmDelete = async () => {
        const { slotId, applicationsCount } = deleteDialog;

        try {
            const response = await fetch(
                `${host}/api/v1/education-slots/${slotId}`,
                {
                    method: "DELETE",
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem(
                            "jwt-vlada"
                        )}`,
                    },
                }
            );

            if (response.ok) {
                const successMessage =
                    applicationsCount > 0
                        ? `Slot i ${applicationsCount} prijava uspešno obrisani. Termini su odblokirani.`
                        : "Slot obrisan i termini su odblokirani";
                showMessage(successMessage, "success");
                fetchEducationSlots();
            } else {
                showMessage("Greška prilikom brisanja slota", "error");
            }
        } catch (error) {
            console.error("Error deleting slot:", error);
            showMessage("Greška prilikom brisanja slota", "error");
        } finally {
            setDeleteDialog({
                show: false,
                slotId: null,
                applicationsCount: 0,
            });
        }
    };

    const cancelDelete = () => {
        setDeleteDialog({ show: false, slotId: null, applicationsCount: 0 });
    };

    const handleEdit = (slot) => {
        setEditingId(slot.id);
        setFormData({
            educationType: slot.educationType,
            slotDate: slot.slotDate,
            startTime: slot.startTime,
            endTime: slot.endTime,
            maxCapacity: slot.maxCapacity,
            availableSlots: slot.availableSlots,
            status: slot.status,
        });
    };

    const resetForm = () => {
        setFormData({
            educationType: "",
            slotDate: "",
            startTime: "",
            endTime: "",
            maxCapacity: 1,
            availableSlots: 1,
            status: "ACTIVE",
        });
    };

    const showMessage = (text, type) => {
        setMessage({ text, type });
        setTimeout(() => setMessage({ text: "", type: "" }), 4000);
    };

    const formatDate = (dateStr) => {
        const date = new Date(dateStr);
        return date.toLocaleDateString("sr-RS");
    };

    const getStatusLabel = (status) => {
        const statusLabels = {
            ACTIVE: "Aktivna",
            CANCELLED: "Otkazana",
            COMPLETED: "Završena",
        };
        return statusLabels[status] || status;
    };

    if (loading) {
        return (
            <div className='admin-education-slots'>
                <h1>Učitavanje...</h1>
            </div>
        );
    }

    return (
        <div className='admin-education-slots'>
            <div className='admin-header'>
                <h1>Upravljanje terminima za edukacije</h1>
                <button
                    className='btn-create'
                    onClick={() => setShowCreateForm(!showCreateForm)}
                >
                    {showCreateForm ? "Otkaži" : "+ Kreiraj novi termin"}
                </button>
            </div>

            {message.text && (
                <div className={`message ${message.type}`}>{message.text}</div>
            )}

            {showCreateForm && (
                <div className='form-container'>
                    <h2>Kreiraj novi termin</h2>
                    <div className='form-grid'>
                        <div className='form-group'>
                            <label>Tip edukacije</label>
                            <select
                                value={formData.educationType}
                                onChange={(e) =>
                                    setFormData({
                                        ...formData,
                                        educationType: e.target.value,
                                    })
                                }
                            >
                                <option value=''>Izaberi tip</option>
                                {Object.entries(educationTypes).map(
                                    ([key, value]) => (
                                        <option key={key} value={key}>
                                            {value}
                                        </option>
                                    )
                                )}
                            </select>
                        </div>
                        <div className='form-group'>
                            <label>Datum</label>
                            <input
                                type='date'
                                value={formData.slotDate}
                                onChange={(e) =>
                                    setFormData({
                                        ...formData,
                                        slotDate: e.target.value,
                                    })
                                }
                            />
                        </div>
                        <div className='form-group'>
                            <label>Početak</label>
                            <input
                                type='time'
                                value={formData.startTime}
                                onChange={(e) =>
                                    setFormData({
                                        ...formData,
                                        startTime: e.target.value,
                                    })
                                }
                            />
                        </div>
                        <div className='form-group'>
                            <label>Kraj</label>
                            <input
                                type='time'
                                value={formData.endTime}
                                onChange={(e) =>
                                    setFormData({
                                        ...formData,
                                        endTime: e.target.value,
                                    })
                                }
                            />
                        </div>
                        <div className='form-group'>
                            <label>Maksimalan broj mesta</label>
                            <input
                                type='number'
                                min='1'
                                value={formData.maxCapacity}
                                onChange={(e) =>
                                    setFormData({
                                        ...formData,
                                        maxCapacity: e.target.value,
                                    })
                                }
                            />
                        </div>
                    </div>
                    <button className='btn-save' onClick={handleCreate}>
                        Kreiraj
                    </button>
                </div>
            )}

            <div className='slots-container'>
                {educationSlots.map((slot) => (
                    <div
                        key={slot.id}
                        className={`slot-card ${
                            slot.status === "CANCELLED" ? "cancelled" : ""
                        }`}
                    >
                        <div className='slot-header'>
                            <h3>
                                {educationTypes[slot.educationType] ||
                                    slot.educationType}
                            </h3>
                            <span
                                className={`status-badge status-${slot.status.toLowerCase()}`}
                            >
                                {getStatusLabel(slot.status)}
                            </span>
                        </div>
                        <div className='slot-details'>
                            <div className='detail-row'>
                                <span className='label'>📅 Datum:</span>
                                <span className='value'>
                                    {formatDate(slot.slotDate)}
                                </span>
                            </div>
                            <div className='detail-row'>
                                <span className='label'>🕐 Vreme:</span>
                                <span className='value'>
                                    {slot.startTime} - {slot.endTime}
                                </span>
                            </div>
                            <div className='detail-row'>
                                <span className='label'>👥 Mesta:</span>
                                <span className='value'>
                                    {slot.maxCapacity - slot.availableSlots} /{" "}
                                    {slot.maxCapacity}
                                </span>
                            </div>
                        </div>
                        <div className='slot-actions'>
                            <button
                                className='btn-edit'
                                onClick={() => handleEdit(slot)}
                            >
                                Izmeni
                            </button>
                            <button
                                className='btn-delete'
                                onClick={() => handleDelete(slot.id)}
                            >
                                Obriši
                            </button>
                        </div>
                    </div>
                ))}
            </div>

            {editingId && (
                <div
                    className='modal-overlay'
                    onClick={() => setEditingId(null)}
                >
                    <div
                        className='modal-content'
                        onClick={(e) => e.stopPropagation()}
                    >
                        <h2>Izmeni termin</h2>
                        <div className='form-grid'>
                            <div className='form-group'>
                                <label>Tip edukacije</label>
                                <select
                                    value={formData.educationType}
                                    onChange={(e) =>
                                        setFormData({
                                            ...formData,
                                            educationType: e.target.value,
                                        })
                                    }
                                >
                                    {Object.entries(educationTypes).map(
                                        ([key, value]) => (
                                            <option key={key} value={key}>
                                                {value}
                                            </option>
                                        )
                                    )}
                                </select>
                            </div>
                            <div className='form-group'>
                                <label>Datum</label>
                                <input
                                    type='date'
                                    value={formData.slotDate}
                                    onChange={(e) =>
                                        setFormData({
                                            ...formData,
                                            slotDate: e.target.value,
                                        })
                                    }
                                />
                            </div>
                            <div className='form-group'>
                                <label>Početak</label>
                                <input
                                    type='time'
                                    value={formData.startTime}
                                    onChange={(e) =>
                                        setFormData({
                                            ...formData,
                                            startTime: e.target.value,
                                        })
                                    }
                                />
                            </div>
                            <div className='form-group'>
                                <label>Kraj</label>
                                <input
                                    type='time'
                                    value={formData.endTime}
                                    onChange={(e) =>
                                        setFormData({
                                            ...formData,
                                            endTime: e.target.value,
                                        })
                                    }
                                />
                            </div>
                            <div className='form-group'>
                                <label>Maksimalan broj mesta</label>
                                <input
                                    type='number'
                                    min='1'
                                    value={formData.maxCapacity}
                                    onChange={(e) =>
                                        setFormData({
                                            ...formData,
                                            maxCapacity: e.target.value,
                                        })
                                    }
                                />
                            </div>
                            <div className='form-group'>
                                <label>Status</label>
                                <select
                                    value={formData.status}
                                    onChange={(e) =>
                                        setFormData({
                                            ...formData,
                                            status: e.target.value,
                                        })
                                    }
                                >
                                    <option value='ACTIVE'>Aktivna</option>
                                    <option value='COMPLETED'>Završena</option>
                                </select>
                            </div>
                        </div>
                        <div className='modal-actions'>
                            <button
                                className='btn-save'
                                onClick={() => handleUpdate(editingId)}
                            >
                                Sačuvaj
                            </button>
                            <button
                                className='btn-cancel'
                                onClick={() => {
                                    setEditingId(null);
                                    resetForm();
                                }}
                            >
                                Otkaži
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Dialog */}
            {deleteDialog.show && (
                <div className='modal-overlay'>
                    <div
                        className='modal-content'
                        style={{ maxWidth: "500px" }}
                    >
                        <h2
                            style={{
                                color:
                                    deleteDialog.applicationsCount > 0
                                        ? "#e74c3c"
                                        : "#be9455",
                            }}
                        >
                            {deleteDialog.applicationsCount > 0
                                ? "⚠️ Upozorenje"
                                : "Potvrda brisanja"}
                        </h2>
                        <div className='form-section'>
                            {deleteDialog.applicationsCount > 0 ? (
                                <div>
                                    <p
                                        style={{
                                            fontSize: "16px",
                                            marginBottom: "15px",
                                            lineHeight: "1.6",
                                        }}
                                    >
                                        Ovaj slot ima{" "}
                                        <strong style={{ color: "#e74c3c" }}>
                                            {deleteDialog.applicationsCount}
                                        </strong>{" "}
                                        aktivnih prijava.
                                    </p>
                                    <p
                                        style={{
                                            fontSize: "16px",
                                            marginBottom: "15px",
                                            lineHeight: "1.6",
                                        }}
                                    >
                                        <strong>
                                            Brisanjem slota ćete obrisati i sve
                                            prijave korisnika za ovaj termin.
                                        </strong>
                                    </p>
                                    <p
                                        style={{
                                            fontSize: "16px",
                                            lineHeight: "1.6",
                                        }}
                                    >
                                        Da li ste sigurni da želite da
                                        nastavite? Termini će biti odblokirani.
                                    </p>
                                </div>
                            ) : (
                                <p
                                    style={{
                                        fontSize: "16px",
                                        lineHeight: "1.6",
                                    }}
                                >
                                    Da li ste sigurni da želite da obrišete ovaj
                                    slot? Termini će biti odblokirani.
                                </p>
                            )}
                        </div>
                        <div className='modal-actions'>
                            <button
                                className='btn-save'
                                onClick={confirmDelete}
                                style={{
                                    backgroundColor:
                                        deleteDialog.applicationsCount > 0
                                            ? "#e74c3c"
                                            : "#be9455",
                                    minWidth: "120px",
                                }}
                            >
                                Da, obriši
                            </button>
                            <button
                                className='btn-cancel'
                                onClick={cancelDelete}
                                style={{ minWidth: "120px" }}
                            >
                                Otkaži
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
